/// <amd-module name="VSSUI/Components/List/ListDragDropBehavior" />
import * as React from "react";
import { KeyCode, noop } from "VSSUI/Util";
import { IBehavior } from "VSSUI/Utilities/Behavior";
import { IEventDispatch } from "VSSUI/Utilities/Dispatch";
import { dispatchCustomDragEvent, DragDropEffect, IDragDataTransfer } from "VSSUI/Utilities/DragDrop";
import { IItemProvider } from "VSSUI/Utilities/Provider";
import { cellFromEvent } from "./List";
import { IDragDroppableUI, IListDragDropSecondaryData } from "./List.Props";
import { IListDragSourceBehaviorOptions, ListDragSourceBehavior } from "./ListDragSourceBehavior";
import { IListDropTargetBehaviorOptions, ListDropTargetBehavior } from "./ListDropTargetBehavior";

export interface IListDragDropBehaviorOptions<D> extends IListDragSourceBehaviorOptions<D>, IListDropTargetBehaviorOptions<D> {}

/**
 * A behavior that combines the ListDragSourceBehavior and ListDropTargetBehavior. If your list
 * is only meant to be a drag source, or only meant to be a drop target, then use those two
 * behaviors individually.
 * In addition to combining the two behaviors for convenience, this single behavior also enhances
 * the list with keyboard drag and drop support.
 */
export class ListDragDropBehavior<D> implements IBehavior<{}, IDragDroppableUI> {
    private dataTransfer: IDragDataTransfer<D, IListDragDropSecondaryData>;
    private dragBehavior: ListDragSourceBehavior<D>;
    private dragItemRowElement: HTMLElement | undefined;
    private dropBehavior: ListDropTargetBehavior<D>;
    private eventDispatch: IEventDispatch;
    private focusIndex: number;
    private itemProvider: IItemProvider<D>;
    private options: IListDragDropBehaviorOptions<D>;

    constructor(options: IListDragDropBehaviorOptions<D>) {
        this.options = options;
        this.dragBehavior = new ListDragSourceBehavior(options);
        this.dropBehavior = new ListDropTargetBehavior(options);
    }

    public initialize = (props: any, dragDroppableUI: IDragDroppableUI, eventDispatch: IEventDispatch) => {
        this.eventDispatch = eventDispatch;
        this.itemProvider = props.itemProvider;

        this.eventDispatch.addEventListener("keydown", this.onKeyDown);

        this.dragBehavior.initialize(props, dragDroppableUI, eventDispatch);
        this.dropBehavior.initialize(props, dragDroppableUI, eventDispatch);
    };

    public componentDidUpdate(props: any) {
        this.itemProvider = props.itemProvider;

        this.dragBehavior.componentDidUpdate(props);
        this.dropBehavior.componentDidUpdate(props);
    }

    public componentWillUnmount() {
        this.eventDispatch?.removeEventListener("keydown", this.onKeyDown);
        if (this.dragItemRowElement) {
            this.dragItemRowElement.removeEventListener("keydown", this.onDragRowKeyDown);
        }

        this.dragBehavior.componentWillUnmount();
        this.dropBehavior.componentWillUnmount();
    }

    private dispatchEventAtIndex(eventType: string, target: HTMLElement, event: KeyboardEvent, index: number) {
        // To maintain consistency with mouse-based drag and drop, we want a way to have the row itself fire
        // the drag events. Since the row element isn't actually associated with the keyboard event that we get,
        // because the drag row itself is still the source of all of these events, we need a way to find that
        // row. We do this by first finding the list of our drag row, and then finding the row with the correct
        // index within that list.

        let listElement: HTMLElement | null = target;
        while (listElement) {
            // We have hit the root of the list, dont look above this.
            if (listElement.classList.contains("bolt-list")) {
                break;
            }
            listElement = listElement.parentElement;
        }
        if (listElement) {
            const rowTarget = listElement.querySelector<HTMLElement>("[data-row-index='" + index + "']");
            if (rowTarget) {
                dispatchCustomDragEvent(eventType, rowTarget, event, this.dataTransfer);
            }
        }
    }

    private endDrag(event: KeyboardEvent, drop = false) {
        dispatchCustomDragEvent("dragend", event.target as HTMLElement, event, this.dataTransfer);
        if (drop) {
            this.dispatchEventAtIndex("drop", event.target as HTMLElement, event, this.focusIndex);
        } else {
            this.dispatchEventAtIndex("dragexit", event.target as HTMLElement, event, this.focusIndex);
        }
        if (this.dragItemRowElement) {
            this.dragItemRowElement.removeEventListener("keydown", this.onDragRowKeyDown);
            this.dragItemRowElement = undefined;
        }
    }

    private fireRowDragEvents(event: KeyboardEvent) {
        this.dispatchEventAtIndex("dragenter", event.target as HTMLElement, event, this.focusIndex);
        this.dispatchEventAtIndex("dragover", event.target as HTMLElement, event, this.focusIndex);
    }

    private onDragRowKeyDown = (event: KeyboardEvent) => {
        if (event.which === KeyCode.escape) {
            this.endDrag(event);
        } else if (event.which === KeyCode.space) {
            this.endDrag(event, true);
        } else if (event.which === KeyCode.downArrow) {
            this.focusIndex = Math.min(this.focusIndex + 1, this.itemProvider.length);
            this.fireRowDragEvents(event);
        } else if (event.which === KeyCode.upArrow) {
            this.focusIndex = Math.max(this.focusIndex - 1, 0);
            this.fireRowDragEvents(event);
        }
        // We don't want the list itself to have a chance to handle these events, while
        // we are in the middle of a drag operation.
        event.preventDefault();
    };

    private onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
        if (!event.defaultPrevented && event.which === KeyCode.space && (event.target as HTMLElement).tagName !== "INPUT") {
            // The user has hit spacebar on the row, so we should start the drag/drop operation.
            const index = cellFromEvent(event).rowIndex;
            this.focusIndex = index;
            const item = this.itemProvider.value[index];
            this.dataTransfer = {
                data: item,
                dropEffect: DragDropEffect.none,
                secondaryData: { index: index, sourceId: this.options.id } as IListDragDropSecondaryData,
                setDragImage: noop,
                type: this.options.type
            };

            // Give the consumer a chance to cancel the drag/drop operation
            dispatchCustomDragEvent("dragstart", event.target, event.nativeEvent, this.dataTransfer);

            if (this.dataTransfer.effectAllowed !== DragDropEffect.none) {
                // As long as the operation was not cancelled, save off the
                // row element that is being dragged and add a keydown listener.
                // This listener will get called before the event dispatch for the list
                // itself and allows us to have a way to no change focus as the user
                // arrows up and down.
                this.dragItemRowElement = event.target as HTMLElement;
                this.dragItemRowElement.addEventListener("keydown", this.onDragRowKeyDown);
            }
        }
    };
}
